import React, { memo, useCallback, useEffect } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { useFeatures } from '@atc/features';

// atc-ui
import {
    Section,
} from 'reaxl';
import { sendClick, sendImpressions } from 'reaxl-analytics';
import { clickTypes } from 'reaxl-analytics-handlers';
import { InventoryCarousel } from 'reaxl-organisms';

import saveListing from '@/utilities/saveListing';

import { inventoryClick, inventoryImpressions } from '@/analytics/srpAnalyticsHandlers';

import {
    kbbVRSDataDuck,
    ownersDuck,
    savedInventoryDuck,
} from '@/ducks';

import {
    bapActiveOwnerDuck,
} from '@/ducks/bap';
import bapSimilarInventoryDuck from '@/ducks/bap/bapSimilarInventoryDuck';
import { vdpSimilarListingsDuck } from '@/ducks/vdp';

// Utilities
import useVdpNavigation from '@/hooks/useVdpNavigation';

export const BuildAndPriceSimilarInventoryCarouselContainer = memo(({
    device,
}) => {
    const activeOwnerId = useSelector(bapActiveOwnerDuck.selectors.getDuckState);
    const owners = useSelector(ownersDuck.selectors.getOwners);
    const activeOwner = owners[activeOwnerId];
    const savedListingIds = useSelector(savedInventoryDuck.selectors.getSavedInventoryIds) || [];
    const trimInfo = useSelector(kbbVRSDataDuck.selectors.getSelectedTrimInfo);
    const listings = useSelector(bapSimilarInventoryDuck.selectors.getSimilarListings);

    const { nationalMSRP, bodyStyle, atcModelCode, trimName } = trimInfo;
    const minPrice = Math.round(nationalMSRP * 0.95);
    const maxPrice = Math.round(nationalMSRP * 1.05);
    const dispatch = useDispatch();
    const hasNewCarBoost = activeOwner?.customOrderStatus?.newCarBoost;

    useEffect(() => {
        if (Object.keys(trimInfo).length > 0 && minPrice && minPrice > 0 && activeOwner) {
            dispatch(bapSimilarInventoryDuck.creators.loadListings({
                ownerId: activeOwner?.id,
                minPrice,
                maxPrice,
                bodyStyleCode: bodyStyle?.code,
                modelCode: atcModelCode,
                trim: trimName,
                hasNewCarBoost,
            }));
        }
    }, [dispatch, trimInfo, minPrice, maxPrice, activeOwner, bodyStyle, atcModelCode, trimName, hasNewCarBoost]);

    const navigateToVdp = useVdpNavigation();
    const {
        personalization: [isSaveEnabled],
    } = useFeatures(['personalization']);

    const handleInventoryListingClick = useCallback(async (event, selectedListing) => {
        // need to stop propagation and prevent default to allow anchors within listing for SEO purposes while using the
        // correct handlers
        event.stopPropagation();
        event.preventDefault();
        const targetClassName = event?.target?.className || [];
        const isSaveClick = targetClassName.includes('glyphicon-heart-o');
        const isSelectClick = targetClassName.includes('input-label') && event.target.innerHTML === 'Select';

        if (!(isSaveClick || isSelectClick) && selectedListing) {
            await sendClick(inventoryClick, event, {
                friendlyPageName: 'Build and Price',
                inventoryId: selectedListing.id,
                par: hasNewCarBoost ? 'ncb_v_lstg' : 'sim_v_lstg',
                pixallData: {
                    eventSource: 'vehicle',
                    pageType: 'buildprice',
                    pageid: 'bld_and_pr',
                },
            });

            if (selectedListing.id) {
                dispatch(vdpSimilarListingsDuck.creators.clear());

                navigateToVdp({
                    listingId: selectedListing.id,
                    clickType: hasNewCarBoost ? clickTypes.NEW_CAR_BOOST : clickTypes.SIMILAR_INVENTORY,
                });
            } else {
                return false;
            }
        }

        return true;
    }, [dispatch, navigateToVdp, hasNewCarBoost]);

    const handleSendImpressions = useCallback((index, selectedListingsIds, startingIndex) => sendImpressions({
        name: inventoryImpressions,
        data: {
            cmp: hasNewCarBoost ? 'ncb_v_lstg' : 'sim_v_lstg_',
            selectedItemIds: selectedListingsIds,
            pixallData: {
                impressionType: hasNewCarBoost ? 'ncb' : 'similar',
                label: `sim_v_lstg_${index}`,
                listingType: hasNewCarBoost ? 'ncb' : 'similar',
                pageType: 'buildprice',
                pageid: 'bld_and_pr',
                rank: index + 1,
            },
            startingIndex,
        },
    }), [hasNewCarBoost]);

    const handleSettle = useCallback((index, _direction, _width, selectedCells) => {
        const subArrayIndex = selectedCells.length * index;
        const selectedListings = listings.slice(subArrayIndex, (subArrayIndex + selectedCells.length));
        const selectedListingsIds = selectedListings.map(({ id }) => id);

        handleSendImpressions(index, selectedListingsIds, subArrayIndex);
    }, [handleSendImpressions, listings]);

    const handleReady = useCallback((index, selectedCells) => {
        const selectedListings = listings.slice(index, (index + selectedCells.length));
        const selectedListingsIds = selectedListings.map(({ id }) => id);

        handleSendImpressions(index, selectedListingsIds, selectedCells.length * index);
    }, [handleSendImpressions, listings]);

    const carouselSettings = {
        onReady: handleReady,
        fullWidthScroll: true,
        visibilityThreshold: 0.9,
    };

    const onSaveToggle = useCallback(async (event, savedListing, wasSaved) => {
        dispatch(saveListing({
            event,
            listing: savedListing,
            parentId: hasNewCarBoost ? 'ncb' : 'sim_v_lstg',
            saveType: { carousel: true },
            sendClick,
            wasSaved,
        }));
    }, [dispatch, hasNewCarBoost]);

    if (listings.length === 0) {
        return null;
    }

    let headingText = 'Similar Vehicles You Might Like';
    if (activeOwner) {
        headingText += ` from ${activeOwner.name}`;
    }

    return (
        <>
            <Section
                alignment="left"
                uiContext="section-2"
                heading={headingText}
                headingClassName="text-blue-darker text-size-lg-500 margin-top-5 padding-horizontal-4"
                className="padding-bottom-1"
            >
                <InventoryCarousel
                    key={activeOwner?.id || 'carousel-bap-id'}
                    orientation={device?.is?.xs ? 'portrait' : 'landscape'}
                    listings={listings}
                    clickType={clickTypes.SIMILAR_INVENTORY}
                    carouselSettings={carouselSettings}
                    onSettle={handleSettle}
                    owner={activeOwner}
                    onSaveToggle={isSaveEnabled && onSaveToggle}
                    onInventoryListingClick={handleInventoryListingClick}
                    savedListingIds={savedListingIds}
                    showFooter={false}
                />
            </Section>
            <hr />
        </>
    );

});

export default BuildAndPriceSimilarInventoryCarouselContainer;
